"use client";

import React, { useState } from "react";
import { ArrowRight, MapPin, TrendingUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
interface CaseStudyItem {
  id: string;
  title: string;
  category: string;
  categoryTab: string;
  result: string;
  location: string;
  description: string;
}
const CASE_STUDIES: CaseStudyItem[] = [{
  id: "cs-1",
  title: "Boutique Hotel, Thailand",
  category: "Boutique",
  categoryTab: "boutique",
  result: "+34% RevPAR",
  location: "Phuket, Thailand",
  description: "+34% RevPAR growth through dynamic weekend pricing and direct OTA alignment."
}, {
  id: "cs-2",
  title: "Urban Hotel, India",
  category: "Business Hotel",
  categoryTab: "business",
  result: "+28% Occupancy",
  location: "Bengaluru, India",
  description: "Restructured corporate rate fences and lifted off-peak occupancy by 28%."
}, {
  id: "cs-3",
  title: "Budget Hotel, Kenya",
  category: "Business Hotel",
  categoryTab: "business",
  result: "+22% Net Revenue",
  location: "Nairobi, Kenya",
  description: "Eliminated distribution leakage and streamlined channel manager connectivity."
}, {
  id: "cs-4",
  title: "Heritage Property, Tanzania",
  category: "Heritage & Eco-Lodge",
  categoryTab: "heritage",
  result: "+41% Average Daily Rate",
  location: "Arusha, Tanzania",
  description: "Targeted high-yield safari tour booking windows to increase ADR significantly."
}, {
  id: "cs-5",
  title: "Eco-Lodge, Zanzibar",
  category: "Heritage & Eco-Lodge",
  categoryTab: "heritage",
  result: "+37% Direct Bookings",
  location: "Zanzibar, Tanzania",
  description: "Implemented length-of-stay controls and premium sustainable experience tiers."
}, {
  id: "cs-6",
  title: "Island Resort, Maldives",
  category: "Resort",
  categoryTab: "resort",
  result: "+45% Total RevPAR",
  location: "North Male Atoll, Maldives",
  description: "Optimized villa upgrade packaging and dynamic international feed rates."
}, {
  id: "cs-7",
  title: "Resort Chain, Maldives",
  category: "Resort",
  categoryTab: "resort",
  result: "+29% Group Portfolio Yield",
  location: "Baa Atoll, Maldives",
  description: "Multi-property yield management system deployment with central rate parity."
}, {
  id: "cs-8",
  title: "Business Hotel, UK",
  category: "Business Hotel",
  categoryTab: "business",
  result: "+31% GOPPAR Increase",
  location: "Manchester, United Kingdom",
  description: "Weekday corporate demand tracking paired with event-driven dynamic ceilings."
}, {
  id: "cs-9",
  title: "Luxury Villa, Philippines",
  category: "Villa",
  categoryTab: "villa",
  result: "+48% Direct Revenue",
  location: "El Nido, Philippines",
  description: "Positioned luxury micro-season rates and optimized direct booking conversions."
}, {
  id: "cs-10",
  title: "5-Star Resort, India",
  category: "Resort",
  categoryTab: "resort",
  result: "+39% Peak Season ADR",
  location: "Goa, India",
  description: "Unbundled experiential amenities and captured premium festive lead times."
}];
const FILTER_TABS = [{
  value: "all",
  label: "All (10)"
}, {
  value: "boutique",
  label: "Boutique (1)"
}, {
  value: "resort",
  label: "Resort (3)"
}, {
  value: "business",
  label: "Business Hotel (3)"
}, {
  value: "heritage",
  label: "Heritage & Eco-Lodge (2)"
}, {
  value: "villa",
  label: "Villa (1)"
}];
export default function CaseStudyDirectory() {
  const [activeTab, setActiveTab] = useState<string>("all");
  const filteredStudies = activeTab === "all" ? CASE_STUDIES : CASE_STUDIES.filter(item => item.categoryTab === activeTab);
  return <section id="casestudydirectory" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3.5 py-1 text-xs uppercase tracking-wider font-semibold border-secondary/40 text-foreground bg-secondary/10 rounded-full">
            Proven Performance
          </Badge>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
            Case study directory
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl leading-relaxed">
            Explore verified yield outcomes and real-world revenue optimization
            benchmarks achieved across independent and chain hospitality assets.
          </p>
        </div>

        {/* Filter Navigation */}
        <div className="flex justify-center mb-12">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-4xl">
            <TabsList className="bg-muted/60 p-1.5 rounded-xl border border-border flex flex-wrap justify-center h-auto gap-1">
              {FILTER_TABS.map(tab => <TabsTrigger key={tab.value} value={tab.value} className="rounded-lg px-3.5 py-2 text-xs sm:text-sm font-medium transition-all data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm text-muted-foreground hover:text-foreground">
                  {tab.label}
                </TabsTrigger>)}
            </TabsList>
          </Tabs>
        </div>

        {/* Case Studies Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredStudies.map((study, index) => <Card key={study.id} data-index={index} className="bg-card text-card-foreground border border-border shadow-md hover:shadow-xl hover:border-secondary/50 transition-all duration-300 flex flex-col justify-between rounded-xl overflow-hidden group">
              <div>
                <CardHeader className="p-6 pb-4 space-y-4">
                  {/* Category and Metric Badges */}
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                      {study.category}
                    </span>
                    <Badge variant="secondary" className="bg-secondary/15 text-foreground border border-secondary/30 font-bold px-2.5 py-1 text-xs flex items-center gap-1 rounded-md">
                      <TrendingUp className="size-3.5 text-foreground" />
                      {study.result}
                    </Badge>
                  </div>

                  {/* Location Info */}
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="size-3.5 shrink-0 text-secondary" />
                    <span>{study.location}</span>
                  </div>

                  {/* Property Title */}
                  <CardTitle className="font-serif text-xl font-bold tracking-tight text-foreground group-hover:text-primary transition-colors">
                    {study.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="px-6 py-2">
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {study.description}
                  </p>
                </CardContent>
              </div>

              <CardFooter className="p-6 pt-4 border-t border-border/60 mt-4">
                <Button asChild variant="ghost" className="w-full justify-between px-0 font-semibold text-foreground hover:text-secondary group/btn transition-colors">
                  <Link to="#" className="flex items-center justify-between w-full">
                    <span>Read Case Study</span>
                    <ArrowRight className="size-4 transform group-hover/btn:translate-x-1 transition-transform text-secondary" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}